import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axiosInstance from "../../../axiosInstance";
import getDecryptData from "../../../helpers/getDecryptData";

const DEFAULT_HEADER_LINKS = [
  { text: "Home", link: "/" },
 
];

let cachedHomeData = null;
let cachedSession = null;
let hasSessionCache = false;

const normalizeLink = (value) => {
  if (!value || typeof value !== "string") return "/";
  if (value.startsWith("http://") || value.startsWith("https://")) return value;
  if (value.startsWith("/")) return value;
  return `/${value}`;
};

const flattenLinks = (items = []) => {
  if (!Array.isArray(items)) return [];

  const result = [];

  const walk = (entryList) => {
    entryList.forEach((entry) => {
      if (entry?.text && entry?.link) {
        result.push({
          text: String(entry.text),
          link: normalizeLink(entry.link),
          target: entry.target || "_self",
        });
      }

      if (Array.isArray(entry?.children) && entry.children.length) {
        walk(entry.children);
      }
    });
  };

  walk(items);
  return result;
};

const normalizeAssetUrl = (value) => {
  if (!value) return "";
  if (String(value).startsWith("http://") || String(value).startsWith("https://")) return value;

  const base = String(axiosInstance.defaults.baseURL || "").replace(/\/$/, "");
  const clean = String(value).replace(/^\/+/, "");

  return base ? `${base}/${clean}` : `/${clean}`;
};

const Header = () => {
  const location = useLocation();
  const [homeData, setHomeData] = useState(null);
  const [session, setSession] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const load = async () => {
      if (cachedHomeData) setHomeData(cachedHomeData);
      if (hasSessionCache) setSession(cachedSession);
      if (cachedHomeData && hasSessionCache) return;

      try {
        const requests = [];

        if (!cachedHomeData) {
          requests.push(axiosInstance.get("/home-data"));
        } else {
          requests.push(Promise.resolve(null));
        }

        if (!hasSessionCache) {
          requests.push(getDecryptData());
        } else {
          requests.push(Promise.resolve(cachedSession));
        }

        const [homeResponse, user] = await Promise.all(requests);

        if (!cachedHomeData) {
          cachedHomeData = homeResponse?.data?.homeData || null;
          setHomeData(cachedHomeData);
        }

        if (!hasSessionCache) {
          cachedSession = user || null;
          hasSessionCache = true;
          setSession(cachedSession);
        }
      } catch (error) {
        if (!hasSessionCache) {
          cachedSession = null;
          hasSessionCache = true;
          setSession(null);
        }
      }
    };

    load();
  }, []);

  useEffect(() => {
    const syncSession = async (event) => {
      const nextSession =
        event?.detail !== undefined ? event.detail || null : await getDecryptData();
      cachedSession = nextSession;
      hasSessionCache = true;
      setSession(nextSession);
    };

    window.addEventListener("ynb-auth-changed", syncSession);
    return () => window.removeEventListener("ynb-auth-changed", syncSession);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const dashboardLink =
    session?.type === 0
      ? "/dashboard"
      : session?.type === 2
      ? "/job-seeker/dashboard"
      : session?.type === 1 || session?.type === 3
      ? "/recruiter/dashboard"
      : "/login";

  const headerLinks = useMemo(() => {
    const dynamic = flattenLinks(homeData?.header);
    return dynamic.length ? dynamic : DEFAULT_HEADER_LINKS;
  }, [homeData?.header]);

  const logoUrl = normalizeAssetUrl(homeData?.meta_logo || "");

  return (
    <>
      <style>{`
        .q-header {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1000;
          transition: all 0.3s ease;
          padding: 0;
        }

        .q-header.scrolled {
          background: #fff;
          box-shadow: 0 2px 20px rgba(0,0,0,0.08);
        }

        .q-header:not(.scrolled) {
          background: rgba(255,255,255,0.97);
          box-shadow: 0 1px 0 #eee;
        }

        .q-header-inner {
          display: flex;
          align-items: center;
          justify-content: space-between;
          height: 68px;
          gap: 16px;
        }

        .q-brand {
          display: flex;
          align-items: center;
          gap: 8px;
          text-decoration: none;
          flex-shrink: 0;
        }

        .q-brand-logo {
          height: 38px;
          object-fit: contain;
        }

        .q-brand-text {
          font-size: 1.4rem;
          font-weight: 800;
          color: #1a1a2e;
          letter-spacing: -0.5px;
        }

        .q-brand-text span {
          color: #6c5ce7;
        }

        .q-nav {
          display: flex;
          align-items: center;
          gap: 4px;
          list-style: none;
          margin: 0;
          padding: 0;
        }

        .q-nav a {
          display: block;
          padding: 6px 13px;
          font-size: 0.875rem;
          font-weight: 500;
          color: #444;
          text-decoration: none;
          border-radius: 8px;
          transition: all 0.2s;
          white-space: nowrap;
        }

        .q-nav a:hover,
        .q-nav a.active {
          color: #6c5ce7;
          background: #f0eeff;
        }

        .q-header-actions {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-shrink: 0;
        }

        .q-btn-signin {
          padding: 7px 18px;
          font-size: 0.875rem;
          font-weight: 600;
          color: #333;
          background: transparent;
          border: 1.5px solid #ddd;
          border-radius: 8px;
          text-decoration: none;
          transition: all 0.2s;
          white-space: nowrap;
        }

        .q-btn-signin:hover {
          border-color: #6c5ce7;
          color: #6c5ce7;
        }

        .q-btn-signup {
          padding: 7px 18px;
          font-size: 0.875rem;
          font-weight: 600;
          color: #fff;
          background: #6c5ce7;
          border: 1.5px solid #6c5ce7;
          border-radius: 8px;
          text-decoration: none;
          transition: all 0.2s;
          white-space: nowrap;
        }

        .q-btn-signup:hover {
          background: #5a4bd1;
          border-color: #5a4bd1;
          color: #fff;
        }

        .q-avatar-circle {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: linear-gradient(135deg, #6c5ce7, #a29bfe);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          font-size: 0.8rem;
          font-weight: 700;
          flex-shrink: 0;
        }

        .q-toggle {
          display: none;
          flex-direction: column;
          gap: 5px;
          cursor: pointer;
          background: none;
          border: none;
          padding: 4px;
          position: relative;
          z-index: 1102;
        }

        .q-toggle span {
          display: block;
          width: 22px;
          height: 2px;
          background: #333;
          border-radius: 2px;
          transition: all 0.3s;
        }

        .q-toggle.open span:nth-child(1) {
          transform: translateY(7px) rotate(45deg);
        }

        .q-toggle.open span:nth-child(2) {
          opacity: 0;
        }

        .q-toggle.open span:nth-child(3) {
          transform: translateY(-7px) rotate(-45deg);
        }

        .q-mobile-panel {
          display: none;
        }

        .q-mobile-backdrop {
          display: none;
        }

        @media (max-width: 991px) {
          .q-nav, .q-header-actions {
            display: none;
          }

          .q-toggle {
            display: flex;
          }

          .q-mobile-panel {
            display: block;
            position: fixed;
            top: 68px;
            left: 0;
            right: 0;
            background: #fff;
            border-top: 1px solid #eee;
            padding: 16px;
            box-shadow: 0 8px 24px rgba(0,0,0,0.1);
            transform: translateY(-110%);
            opacity: 0;
            visibility: hidden;
            pointer-events: none;
            transition: all 0.3s ease;
            z-index: 1101;
          }

          .q-mobile-panel.open {
            transform: translateY(0);
            opacity: 1;
            visibility: visible;
            pointer-events: auto;
          }

          .q-mobile-backdrop {
            display: block;
            position: fixed;
            top: 68px;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(15, 23, 42, 0.28);
            border: 0;
            opacity: 0;
            pointer-events: none;
            transition: opacity 0.25s ease;
            z-index: 1100;
          }

          .q-mobile-backdrop.open {
            opacity: 1;
            pointer-events: auto;
          }

          .q-mobile-nav {
            list-style: none;
            padding: 0;
            margin: 0 0 16px;
          }

          .q-mobile-nav a {
            display: block;
            padding: 10px 12px;
            color: #333;
            text-decoration: none;
            font-weight: 500;
            border-radius: 8px;
            transition: background 0.2s;
          }

          .q-mobile-nav a:hover {
            background: #f5f3ff;
            color: #6c5ce7;
          }

          .q-mobile-actions {
            display: flex;
            gap: 10px;
          }

          .q-mobile-actions a {
            flex: 1;
            text-align: center;
          }
        }
      `}</style>

      <header className={`q-header${scrolled ? " scrolled" : ""}`}>
        <div className="container">
          <div className="q-header-inner">
            <Link to="/" className="q-brand">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={homeData?.meta_title || "Logo"}
                  className="q-brand-logo"
                />
              ) : (
                <span className="q-brand-text">
                  ....
                </span>
              )}
            </Link>

            <nav>
              <ul className="q-nav">
                {headerLinks.map((item) => (
                  <li key={`${item.link}-${item.text}`}>
                    {item.link.startsWith("http") ? (
                      <a href={item.link} target={item.target} rel="noopener noreferrer">
                        {item.text}
                      </a>
                    ) : (
                      <Link
                        to={item.link}
                        className={location.pathname === item.link ? "active" : ""}
                      >
                        {item.text}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </nav>

            <div className="q-header-actions">
              {session?._id ? (
                <>
                  <div className="q-avatar-circle">
                    {String(session.name || "U")[0].toUpperCase()}
                  </div>
                  <Link to={dashboardLink} className="q-btn-signup">
                    Dashboard
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/login" className="q-btn-signin">
                    Sign In
                  </Link>
                  <Link to="/register" className="q-btn-signup">
                    Sign Up
                  </Link>
                </>
              )}
            </div>

            <button
              type="button"
              className={`q-toggle${isMenuOpen ? " open" : ""}`}
              onClick={() => setIsMenuOpen((prev) => !prev)}
              aria-label="Toggle menu"
              aria-expanded={isMenuOpen}
              aria-controls="public-mobile-menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        </div>

        <button
          type="button"
          className={`q-mobile-backdrop${isMenuOpen ? " open" : ""}`}
          aria-label="Close menu"
          onClick={() => setIsMenuOpen(false)}
        />

        <div
          id="public-mobile-menu"
          className={`q-mobile-panel${isMenuOpen ? " open" : ""}`}
        >
          <ul className="q-mobile-nav">
            {headerLinks.map((item) => (
              <li key={`m-${item.link}-${item.text}`}>
                {item.link.startsWith("http") ? (
                  <a
                    href={item.link}
                    target={item.target}
                    rel="noopener noreferrer"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.text}
                  </a>
                ) : (
                  <Link to={item.link} onClick={() => setIsMenuOpen(false)}>
                    {item.text}
                  </Link>
                )}
              </li>
            ))}
          </ul>

          <div className="q-mobile-actions">
            {session?._id ? (
              <Link
                to={dashboardLink}
                className="q-btn-signup"
                onClick={() => setIsMenuOpen(false)}
              >
                Dashboard
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="q-btn-signin"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="q-btn-signup"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
